import { ComponentType } from '@angular/cdk/portal';
import { HttpParams } from '@angular/common/http';
import {
    AfterViewInit, ChangeDetectorRef, Component, ContentChildren,
    ElementRef, HostListener, Inject, Input, NgZone, OnDestroy, OnInit,
    QueryList, ViewChild,
    forwardRef,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import * as _ from 'lodash';
import * as moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { Permissions } from '../../common/constants';
import { CUSTOM_FILTERS_AND_COMPONENTS_MAP } from '../../common/setup';
import { BulkUpdateParentThingsResponse, Location, Thing } from '../../model';
import { BulkUpdateType } from '../../model/bulk-update';
import { MapFilterRequiredData } from '../../model/map-filter-required-data';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { CustomLabelService } from '../../service/custom-label.service';
import { NavigationService } from '../../service/navigation.service';
import { ThingService } from '../../service/thing.service';
import { UserThingService } from '../../service/user-thing.service';
import { CompositePartComponent, PropertyComponent } from '../../shared/component';
import { BulkParentAssignDialogComponent, BulkParentAssignDialogData } from '../../shared/component/bulk-parent-assign-dialog/bulk-parent-assign-dialog.component';
import { BulkUpdateTagDialogComponent, BulkUpdateTagDialogData } from '../../shared/component/bulk-update-tag-dialog/bulk-update-tag-dialog.component';
import { LoaderPipe, LocalizationPipe } from '../../shared/pipe';
import { COMPONENT_DEFINITION_REF } from '../../shared/utility/component-definition-token';
import { BulkOperationDialogComponent, BulkOperationDialogData } from '../bulk-operation-dialog/bulk-operation-dialog.component';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { ConnectionStatusMapFilter } from './map-filter/connection-status-map-filter';
import { IssueMapFilter } from './map-filter/issue-map-filter';
import { MapFilter } from './map-filter/map-filter';
import { MapFilterComponent } from './map-filter/map-filter.component';
import { StatusMapFilter } from './map-filter/status-map-filter';
import { LocationForMap, MapService, ThingForMap } from './map.service';

require('./Leaflet.SelectAreaFeature.js');
require('../../../../node_modules/leaflet/dist/leaflet.css');
require('../../../../node_modules/leaflet.markercluster/dist/MarkerCluster.css');

const DEFAULT_HEIGHT = 600;
const DEFAULT_MAP_LAYER_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const HIERARCHY_COLOR_PALETTE = ['#F94144', '#00BFFF', '#F3722C', '#43AA8B', '#F8961E', '#4D908E', '#F9844A', '#577590', '#F9C74F', '#277DA1'];

@Component({
    selector: 'map-widget',
    template: require('./map.component.html'),
    styles: [require('./map.component.css')],
    providers: [MapService]
})
export class MapComponent implements AfterViewInit, OnInit, OnDestroy {

    @Input() context: MapContext = MapContext.THINGS;

    @Input() title: string;

    @Input() moveElementEnabled: boolean = false;

    @Input() showHierarchyEnabled: boolean = false;

    @Input() bulkControlsEnabled: boolean = false;

    @Input() queryFieldRef: string;

    @Input() refreshInterval: string = 'PT300S'; // 300 secs (5 min) default

    @Input() query: { property: string, predicate: string, value: any }[];

    @Input() searchFields: string[];

    @Input() maxClusterRadius: number = 40;

    @Input() disableClusteringAtZoomLevel: number;

    @Input() maxZoomLevel: number = 18;

    @Input() mapLayerUrl: string = DEFAULT_MAP_LAYER_URL;

    @Input() tooltipClass: string = 'map-tooltip';

    @Input() styleClass: string;

    @Input() height: string;

    @Input() description: string;

    @Input() showRootThings: boolean;

    @Input() mapLayerFunction: string;

    @ContentChildren(MapFilterComponent) mapFilters: QueryList<MapFilterComponent>;

    @ContentChildren(COMPONENT_DEFINITION_REF) properties: QueryList<PropertyComponent | CompositePartComponent>;

    @ViewChild('mapContainer') private mapContainer: ElementRef;

    private map: any;
    private mapInitialized: boolean = false;
    private markersCluster: any = null;
    private markersHierarchy: any = null;
    private objects: (ThingForMap | LocationForMap)[] = [];
    private objectsIdMapping: { [objectId: string]: ThingForMap | LocationForMap } = {};
    private filteredObjects: (ThingForMap | LocationForMap)[] = [];
    private mapFilterMapping: { [filterName: string]: MapFilter } = {};
    private mapFilterData: { [key: string]: any[] } = {};
    private mapPreselectedStates: { [key: string]: any[] } = {};
    private mapFilter: MapFilter;
    private refreshIntervalMillis: number;
    private intervalId: any;
    private storedFilterKey: string;
    private advancedSearchBody: any;
    private bounds: Bounds;
    private prevMapAction: MapAction = null;
    private controlKeyPressed: boolean = false;
    private selectedAreas: L.latLng[][] = [];
    private manuallySelectedMarkers: L.latLng[] = [];
    private allRootThings: Thing[];
    private fetchGeneration: number = 0;
    private fitMapToContentOnReload: boolean = false;
    private _unregisterPrintResize: (() => void) | null = null;
    private activeTooltipMarker: any;
    private activeTooltipElement: HTMLElement;
    private onMapMoveStartOrZoomStart: () => void;
    private readonly tooltipPropagationStopEventTypes: string[] = [
        'pointerdown', 'pointerup', 'touchstart', 'touchend', 'mousedown', 'mouseup',
        'dblclick', 'contextmenu'
    ];
    private tooltipCloseTimeoutId: any;
    private tooltipMaxIntentTimeoutId: any;
    private tooltipMouseMoveListener: (event: MouseEvent) => void;
    private tooltipMouseEnterListener: (event: MouseEvent) => void;
    private tooltipMouseLeaveListener: (event: MouseEvent) => void;
    private activeTooltipPropagationListeners: { element: HTMLElement; type: string; listener: EventListener; capture: boolean }[] = [];
    private activeTooltipLinkClickListener: ((event: MouseEvent) => void) | null = null;
    private customFilters: any = CUSTOM_FILTERS_AND_COMPONENTS_MAP;

    mapFilterEntries: any[] = [];
    mapFilterRanks: number[] = [];
    selectedFilter: string;
    selectedMapAction: MapAction;
    selectedIds: string[] = [];
    rankDescriptors: any[];
    objectCount: number = 0;
    initLoading: boolean;
    loading: boolean;
    advancedSearchOpen: boolean = false;
    isMobile: boolean;
    error: string;
    noMarkerMessage: string;
    hierarchyFilterEnabled: boolean = false;
    hierarchyParentColorMapping: { [parentId: string]: string } = {};
    setTagPermission: boolean;
    bulkCommandPermission: boolean;
    bulkRecipePermission: boolean;
    bulkFirmwarePermission: boolean;
    assignParentThingPermission: boolean;
    hierarchyButtonEnabled: boolean;
    setTagButtonEnabled: boolean;
    bulkCommandButtonEnabled: boolean;
    bulkRecipeButtonEnabled: boolean;
    bulkFirmwareButtonEnabled: boolean;
    assignParentThingButtonEnabled: boolean;

    readonly bulkSelectionIcon: string = '/img/bulk_selection.png';
    readonly bulkSelectionActiveIcon: string = '/img/bulk_selection_active.png';
    readonly moveIcon: string = '/img/move.png';
    readonly moveActiveIcon: string = '/img/move_active.png';

    constructor(@Inject(forwardRef(() => MapService)) private mapService: MapService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => AppService)) private appService: AppService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => CustomLabelService)) private labelService: CustomLabelService,
        @Inject(forwardRef(() => NgZone)) private zone: NgZone,
        @Inject(forwardRef(() => MatSnackBar)) private snackBar: MatSnackBar,
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => LoaderPipe)) private loaderPipe: LoaderPipe,
        @Inject(forwardRef(() => UserThingService)) private userThingService: UserThingService,
        @Inject(forwardRef(() => ChangeDetectorRef)) private changeDetector: ChangeDetectorRef,
        @Inject(forwardRef(() => ElementRef)) private elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService
    ) { }

    ngOnInit(): void {
        this.refreshIntervalMillis = Math.max(moment.duration(this.refreshInterval).asMilliseconds(), 5000); // min 5 sec
        this.height = this.height || (DEFAULT_HEIGHT + 'px');
        this.initLoading = true;
        this.checkIsMobile();
        this.checkPermissions();
        if (!this.searchFields) {
            if (this.context == MapContext.THINGS) {
                this.searchFields = ['name', 'serialNumber', 'customer.name', 'customer.code'];
            } else {
                this.searchFields = ['name'];
            }
        }
        this.maxZoomLevel = this.maxZoomLevel != null && this.maxZoomLevel <= 18 ? this.maxZoomLevel : 18;
        this._unregisterPrintResize = this.widgetPrintResizeCoordinator.subscribeElement(this.elementRef, () => this.updateBeforePrint());
    }

    ngAfterViewInit(): void {
        let storedFieldKey: string;
        if (this.context == MapContext.THINGS) {
            storedFieldKey = 'thingAdvancedSearchFieldsValues';
            this.storedFilterKey = 'thingFilterValue';
        } else {
            storedFieldKey = 'locationAdvancedSearchFieldsValues';
            this.storedFilterKey = 'locationFilterValue';
        }

        const storedFieldsValues = localStorage.getItem(this.queryFieldRef || storedFieldKey);
        const savedFieldsValues = storedFieldsValues ? JSON.parse(storedFieldsValues) : null;
        if (!this.queryFieldRef && !this.query && !savedFieldsValues) {
            this.fetchData(true);
        }
    }

    ngOnDestroy(): void {
        this._unregisterPrintResize?.();
        this._unregisterPrintResize = null;
        this.fetchGeneration++;
        if (this.map) {
            this.map.off('click', this.onMapClick, this);
            this.map.off('movestart zoomstart', this.onMapMoveStartOrZoomStart);
        }
        this.closeActiveTooltip();
        this.clearRefreshInterval();
    }

    private checkIsMobile(): void {
        this.isMobile = this.appService.isMobile();
    }

    private checkPermissions(): void {
        if (this.context == MapContext.THINGS) {
            this.setTagPermission = this.authenticationService.hasPermission(Permissions.WRITE_THING_TAG);
            this.bulkRecipePermission = this.authenticationService.hasPermission(Permissions.EXECUTE_BULK_UPDATE) && this.authenticationService.hasPermission(Permissions.EXECUTE_THING_COMMAND);
            this.bulkCommandPermission = this.authenticationService.hasPermission(Permissions.EXECUTE_BULK_UPDATE) && this.authenticationService.hasPermission(Permissions.EXECUTE_THING_COMMAND);
            this.bulkFirmwarePermission = this.authenticationService.hasPermission(Permissions.EXECUTE_BULK_UPDATE) && this.authenticationService.hasPermission(Permissions.UPDATE_FIRMWARE);
            this.assignParentThingPermission = this.authenticationService.hasPermission(Permissions.WRITE_THING);

            this.moveElementEnabled = this.moveElementEnabled && this.authenticationService.hasPermission(Permissions.WRITE_THING);
            this.bulkControlsEnabled = this.bulkControlsEnabled && (this.setTagPermission || this.bulkRecipePermission || this.bulkCommandPermission || this.bulkFirmwarePermission || this.assignParentThingPermission);
        } else {
            this.moveElementEnabled = this.moveElementEnabled && this.authenticationService.hasPermission(Permissions.WRITE_LOCATION);
            this.bulkControlsEnabled = false;
        }
    }

    private initRefreshInterval(): void {
        this.clearRefreshInterval();
        this.zone.runOutsideAngular(() => {
            this.intervalId = setInterval(() => {
                this.zone.run(() => {
                    this.fetchData();
                });
            }, this.refreshIntervalMillis);
        });
    }

    private clearRefreshInterval(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    private async fetchData(fitMapToContent: boolean = false): Promise<void> {
        const generation = ++this.fetchGeneration;
        this.fitMapToContentOnReload = fitMapToContent;
        this.clearRefreshInterval();
        if (this.error == 'fetchingMapDataErrorProperty') {
            this.error = null;
        }
        try {
            if (!this.mapInitialized) {
                this.mapFilterMapping = {};
                this.mapPreselectedStates = {};
                this.mapFilterEntries = [];
                let promises = [this.buildMapFilterMap()];
                if (this.context == MapContext.THINGS && this.assignParentThingPermission) {
                    let params = new HttpParams();
                    params = this.mapService.buildContextParams(params);
                    params = params.set('rootsOnly', true);
                    promises.push(this.userThingService.getRecursivelyAllThings(null, [], null, null, null, params).catch(() => []).then(things => this.allRootThings = things));
                }
                await Promise.all(promises);
                if (!this.isCurrentFetch(generation)) {
                    return;
                }
                await this.initialFetchData(generation);
            } else {
                await this.refreshFetchData(generation);
            }
        } catch (error) {
            if (this.isCurrentFetch(generation)) {
                this.error = 'fetchingMapDataErrorProperty';
                this.loading = false;
                this.initLoading = false;
                this.refreshMapView();
            }
        } finally {
            if (this.isCurrentFetch(generation)) {
                this.fitMapToContentOnReload = false;
            }
        }
    }

    private async initialFetchData(generation: number): Promise<void> {
        const isLocationContext = this.context == MapContext.LOCATIONS;
        const requiredData = this.computeMapFilterRequiredData();
        const initialData = await this.fetchInitialMapData(generation, requiredData, isLocationContext);
        if (!initialData) {
            return;
        }

        this.publishInitialMapData(initialData, isLocationContext);
        this.showMapContainer();
        await this.waitForAngularRender();
        if (!this.isCurrentFetch(generation)) {
            return;
        }
        this.initMap();
        await this.loadInitialMapMarkers(generation, requiredData);
        if (this.isCurrentFetch(generation)) {
            this.initRefreshInterval();
        }
    }

    private async fetchInitialMapData(generation: number, requiredData: MapFilterRequiredData, isLocationContext: boolean): Promise<InitialMapData> {
        const filterDataKeys: string[] = [];
        const filterDataPromises: Promise<any>[] = [];
        const locationObjectsPromise: Promise<LocationForMap[]> = isLocationContext
            ? this.mapService.getAllLocations(this.advancedSearchBody, this.searchFields)
            : Promise.resolve([]);

        if (!isLocationContext && requiredData.locations) {
            filterDataPromises.push(this.mapService.getAllLocations());
            filterDataKeys.push('locations');
        }
        if (requiredData.alerts) {
            filterDataPromises.push(this.mapService.getAllAlerts());
            filterDataKeys.push('alerts');
        }
        if (requiredData.events) {
            filterDataPromises.push(this.mapService.getAllEvents());
            filterDataKeys.push('events');
        }
        const [locationObjects, filterDataResults] = await Promise.all([
            locationObjectsPromise,
            Promise.all(filterDataPromises)
        ]);
        if (!this.isCurrentFetch(generation)) {
            return null;
        }
        const filterData: { [key: string]: any[] } = {};
        filterDataKeys.forEach((item, index) => {
            filterData[item] = filterDataResults[index];
        });
        return { locationObjects, filterData };
    }

    private publishInitialMapData(initialData: InitialMapData, isLocationContext: boolean): void {
        const objects: (ThingForMap | LocationForMap)[] = [];
        const objectsIdMapping: { [objectId: string]: ThingForMap | LocationForMap } = {};
        if (isLocationContext) {
            initialData.locationObjects.forEach(obj => this.addValidObjectToSnapshot(obj, objects, objectsIdMapping));
        }
        this.objects = objects;
        this.objectsIdMapping = objectsIdMapping;
        if (isLocationContext) {
            this.checkEmptyObjects();
        } else {
            this.noMarkerMessage = null;
        }
        this.mapFilterData = initialData.filterData;
    }

    private showMapContainer(): void {
        this.initLoading = false;
        this.refreshMapView();
    }

    private async loadInitialMapMarkers(generation: number, requiredData: MapFilterRequiredData): Promise<void> {
        if (this.context == MapContext.THINGS || requiredData.things) {
            await this.fetchThings(generation);
        } else {
            this.reloadMapMarkers();
        }
    }

    private async fetchThings(generation: number): Promise<void> {
        this.loading = true;
        const loadedObjects: (ThingForMap | LocationForMap)[] = this.context == MapContext.THINGS ? [] : [...this.objects];
        const loadedObjectsIdMapping: { [objectId: string]: ThingForMap | LocationForMap } = this.context == MapContext.THINGS ? {} : Object.assign({}, this.objectsIdMapping);
        let loadedThings: Thing[] = this.context == MapContext.THINGS ? null : [];
        try {
            const generator = (this.context == MapContext.THINGS) ? this.mapService.getThingsInChunks(5, this.advancedSearchBody, this.searchFields, this.showRootThings) : this.mapService.getThingsInChunks(5, null, null, this.showRootThings);
            for await (const objects of generator) {
                if (!this.isCurrentFetch(generation)) {
                    return;
                }
                if (this.context == MapContext.THINGS) {
                    objects.forEach(obj => this.addValidObjectToSnapshot(obj, loadedObjects, loadedObjectsIdMapping));
                } else {
                    loadedThings = loadedThings.concat(objects.map(obj => obj as Thing));
                }
                this.objects = loadedObjects;
                this.objectsIdMapping = loadedObjectsIdMapping;
                if (loadedThings) {
                    this.mapFilterData['things'] = loadedThings;
                }
                this.checkEmptyObjects();
                this.reloadMapMarkers();
            }
        } catch (error) {
            if (this.isCurrentFetch(generation)) {
                this.error = 'fetchingMapDataErrorProperty';
            }
        } finally {
            if (this.isCurrentFetch(generation)) {
                this.loading = false;
                this.refreshMapView();
            }
        }
    }

    private async refreshFetchData(generation: number): Promise<void> {
        this.loading = true;
        this.clearRefreshInterval();
        const dataKeys = [];
        try {
            const results = await this.buildRefreshFetchPromise(dataKeys);
            if (!this.isCurrentFetch(generation)) {
                return;
            }
            const objects: (ThingForMap | LocationForMap)[] = [];
            const objectsIdMapping: { [objectId: string]: ThingForMap | LocationForMap } = {};
            results[0].forEach(obj => this.addValidObjectToSnapshot(obj, objects, objectsIdMapping));
            this.mapFilterData = {};
            dataKeys.forEach((item, index) => {
                this.mapFilterData[item] = results[index + 1];
            });
            this.objects = objects;
            this.objectsIdMapping = objectsIdMapping;
            this.checkEmptyObjects();
            this.reloadMapMarkers();
        } catch (error) {
            if (this.isCurrentFetch(generation)) {
                this.error = 'fetchingMapDataErrorProperty';
            }
        } finally {
            if (this.isCurrentFetch(generation)) {
                this.loading = false;
                this.refreshMapView();
                this.initRefreshInterval();
            }
        }
    }

    private buildRefreshFetchPromise(dataKeys: string[]): Promise<any[]> {
        const requiredData = this.computeMapFilterRequiredData();
        let dataPromises: Promise<any>[] = [];
        if (this.context == MapContext.THINGS) {
            dataPromises.push(this.mapService.getAllThings(this.advancedSearchBody, this.searchFields, this.showRootThings));
            if (requiredData.locations) {
                dataPromises.push(this.mapService.getAllLocations());
                dataKeys.push('locations');
            }
        } else {
            dataPromises.push(this.mapService.getAllLocations(this.advancedSearchBody, this.searchFields));
            if (requiredData.things) {
                dataPromises.push(this.mapService.getAllThings(null, null, this.showRootThings));
                dataKeys.push('things');
            }
        }
        if (requiredData.alerts) {
            dataPromises.push(this.mapService.getAllAlerts());
            dataKeys.push('alerts');
        }
        if (requiredData.events) {
            dataPromises.push(this.mapService.getAllEvents());
            dataKeys.push('events');
        }
        return Promise.all(dataPromises);
    }

    private addValidObjectToSnapshot(obj: ThingForMap | LocationForMap, objects: (ThingForMap | LocationForMap)[], objectsIdMapping: { [objectId: string]: ThingForMap | LocationForMap }): void {
        if (obj.latLng) {
            objects.push(obj);
            objectsIdMapping[obj.id] = obj;
        }
    }

    private isCurrentFetch(generation: number): boolean {
        return generation == this.fetchGeneration;
    }

    private waitForAngularRender(): Promise<void> {
        return firstValueFrom(this.zone.onStable.pipe(take(1)));
    }

    private refreshMapView(): void {
        this.zone.run(() => this.changeDetector.markForCheck());
    }

    private checkEmptyObjects(): void {
        if (!this.objects.length) {
            if (this.context == MapContext.THINGS) {
                this.noMarkerMessage = 'noThingMarkerProperty'
            } else {
                this.noMarkerMessage = 'noLocationMarkerProperty'
            }
        } else {
            this.noMarkerMessage = null;
            this.selectedIds.forEach((id) => {
                if (this.objectsIdMapping[id]) {
                    this.objectsIdMapping[id].selected = true;
                }
            });
        }
    }

    private drawMap(): void {
        if (!this.mapContainer?.nativeElement) {
            return;
        }
        this.map = L.map(this.mapContainer.nativeElement, {
            center: [45.697127, 9.035056],
            zoom: 6,
            zoomControl: false,
            dragging: !this.isMobile
        });
        this.map.attributionControl.setPrefix('');

        if (this.isMobile) {
            L.Control.ToggleDragging = L.Control.extend({
                options: {
                    position: 'topright'
                },
                onAdd: this.createToggleDraggingButton.bind(this)
            });
            (new L.Control.ToggleDragging).addTo(this.map);
        } else {
            let zoomControl = L.control.zoom({
                position: 'topright'
            });
            zoomControl.addTo(this.map);
            zoomControl.getContainer().classList.add('user-select-none');
        }
        this.onMapMoveStartOrZoomStart = () => this.closeActiveTooltip();
        this.map.on('movestart zoomstart', this.onMapMoveStartOrZoomStart);
        if (this.isMobile) {
            this.map.on('click', this.onMapClick, this);
        }

        L.Control.FitMapToContent = L.Control.extend({
            options: {
                position: 'topright'
            },
            onAdd: this.createFitToContentButton.bind(this)
        });
        (new L.Control.FitMapToContent).addTo(this.map);

        if (this.mapLayerFunction && this.customFilters[this.mapLayerFunction]) {
            const options = {
                maxZoom: this.maxZoomLevel,
                minZoom: 3,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                mapLayerUrl: this.mapLayerUrl
            }
            this.customFilters[this.mapLayerFunction](this.map, options);
        } else {
            const tiles = L.tileLayer(this.mapLayerUrl, {
                maxZoom: this.maxZoomLevel,
                minZoom: 3,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            });
            tiles.addTo(this.map);
        }
        let clusterOptions = {
            iconCreateFunction: this.createClusterIcon.bind(this)
        }
        if (this.disableClusteringAtZoomLevel) {
            clusterOptions['disableClusteringAtZoom'] = this.disableClusteringAtZoomLevel;
            clusterOptions['spiderfyOnMaxZoom'] = false;
        }
        if (this.maxClusterRadius) {
            clusterOptions['maxClusterRadius'] = this.maxClusterRadius;
        }
        this.markersCluster = L.markerClusterGroup(clusterOptions);
        this.map.addLayer(this.markersCluster);
        this.markersHierarchy = L.layerGroup();
        this.map.addLayer(this.markersHierarchy);
        this.map.selectAreaFeature.onDrawEnd = this.onMapBulkSelectionDrawEnd.bind(this);
    }

    private initMap(): void {
        const storedFilterValue = localStorage.getItem(this.storedFilterKey);
        this.drawMap();
        const storedFilter = this.mapFilterMapping[storedFilterValue];
        if (storedFilter) {
            this.mapFilter = storedFilter;
            this.selectedFilter = storedFilterValue;
        } else {
            this.mapFilter = Object.values(this.mapFilterMapping)[0];
            this.selectedFilter = Object.keys(this.mapFilterMapping)[0];
            localStorage.setItem(this.storedFilterKey, this.selectedFilter);
        }
        this.rankDescriptors = this.mapFilter.getRankDescriptors(this.objects).map(rd => Object.assign({}, rd));
        this.mapFilterRanks = this.getPreselectedRanks(this.selectedFilter, this.rankDescriptors);
        this.mapInitialized = true;
    }

    private buildMapFilterMap(): Promise<any[]> {
        let initPromises = [];
        if (this.mapFilters.length) {
            let filtersMap = {}
            this.mapFilters.forEach(f => {
                try {
                    const splitIndex = f.name.indexOf(':');
                    let name, filterConf;
                    if (splitIndex > -1) {
                        [name, filterConf] = [f.name.slice(0, splitIndex), f.name.slice(splitIndex + 1)];
                    } else {
                        name = f.name;
                    }
                    let preselectedStates;
                    let confObj = null;
                    if (filterConf) {
                        confObj = JSON.parse(filterConf.replaceAll("'", '"'));
                        preselectedStates = confObj?.preselectedStates;
                    }
                    confObj = Object.assign({}, confObj || {}, f.config || {});
                    const filterInstance = this.instantiateFilter(name, confObj);
                    let count;
                    if (name in filtersMap) {
                        count = filtersMap[name]
                    } else {
                        count = 0
                    }
                    filtersMap[name] = count + 1
                    const id = `${name}_${String(count).padStart(2, '0')}`;
                    this.mapFilterMapping[id] = filterInstance;
                    this.mapPreselectedStates[id] = preselectedStates || [];
                    this.mapFilterEntries.push({
                        'id': id,
                        'label': filterInstance.getConfig()?.label || name
                    });
                    filterInstance.setContext(this.context);
                    if (filterInstance.onInit) {
                        initPromises.push(filterInstance.onInit());
                    }
                } catch (e) {
                    console.log(e);
                    this.error = 'parsingMapFilterErrorProperty';
                }
            });
        } else {
            const connectionStatusFilter = new ConnectionStatusMapFilter(null);
            this.mapFilterMapping['ConnectionStatusMapFilter'] = connectionStatusFilter;
            this.mapFilterEntries.push({
                'name': 'ConnectionStatusMapFilter',
                'label': connectionStatusFilter.getConfig()?.label || 'ConnectionStatusMapFilter'
            });
            connectionStatusFilter.setContext(this.context);
        }
        return Promise.all(initPromises);
    }

    private getPreselectedRanks(filterName: string, filterDescriptor: object[]): number[] {
        const preselectedStates = this.mapPreselectedStates[filterName];
        if (preselectedStates) {
            let preselectedRanks = []
            filterDescriptor.forEach(fd => {
                if (preselectedStates.includes(fd['value'])) {
                    preselectedRanks.push(fd['rank'])
                }
            });
            return preselectedRanks;
        } else {
            return [];
        }
    }

    private computeMapFilterRequiredData(): MapFilterRequiredData {
        const requireData = new MapFilterRequiredData();
        Object.values(this.mapFilterMapping).forEach(f => {
            const currentRequiredData = f.getRequiredData();
            requireData.things = requireData.things || currentRequiredData.things;
            requireData.locations = requireData.locations || currentRequiredData.locations;
            requireData.alerts = requireData.alerts || currentRequiredData.alerts;
            requireData.events = requireData.events || currentRequiredData.events;
        });
        return requireData;
    }

    private instantiateFilter(filterName: string, config: any): MapFilter {
        switch (filterName) {
            case 'ConnectionStatusMapFilter':
                return new ConnectionStatusMapFilter(config);
            case 'StatusMapFilter':
                return new StatusMapFilter(config);
            case 'IssueMapFilter':
                return new IssueMapFilter(config);
            default:
                return new CUSTOM_FILTERS_AND_COMPONENTS_MAP[filterName](config) as MapFilter;
        }
    }

    onMapFilterChange(): void {
        const nextMapFilter = this.mapFilterMapping[this.selectedFilter];
        if (!this.selectedFilter || !nextMapFilter) {
            return;
        }
        if (nextMapFilter == this.mapFilter) {
            return;
        }
        this.loading = true;
        localStorage.setItem(this.storedFilterKey, this.selectedFilter);
        this.mapFilter = nextMapFilter;
        const defaultRankDescriptors = this.mapFilter.getRankDescriptors(this.objects).map(rd => Object.assign({}, rd));
        this.mapFilterRanks = this.getPreselectedRanks(this.selectedFilter, defaultRankDescriptors);
        this.reloadMapMarkers();
        this.disableMapAction(this.selectedMapAction);
        this.selectedMapAction = null;
        this.prevMapAction = null;
        this.loading = false;
        this.refreshMapView();
    }

    onMapFilterButtonChange(): void {
        this.loading = true;
        this.disableMapAction(this.selectedMapAction);
        this.selectedMapAction = null;
        this.prevMapAction = null;
        this.applyMapFilter();
        this.loading = false;
        this.refreshMapView();
    }

    onHierarchyToggleChange(): void {
        if (this.hierarchyFilterEnabled) {
            this.drawHierarchyLines();
        } else {
            this.markersHierarchy?.clearLayers();
        }
    }

    private reloadMapMarkers(): void {
        if (!this.mapFilter) {
            return;
        }
        this.mapFilter.computeRank(this.objects, this.mapFilterData);
        const rankDescriptors = this.mapFilter.getRankDescriptors(this.objects).map(rd => Object.assign({}, rd));
        this.rankDescriptors = rankDescriptors.map(rd => {
            const count = this.objects.filter(obj => obj.rank == rd.rank).length;
            return Object.assign({}, rd, {
                label: this.localizationPipe.transform(rd.label) + ` (${count})`
            });
        });
        this.applyMapFilter();
        if (this.fitMapToContentOnReload) {
            this.fitMapToBounds();
        }
        this.refreshMapView();
    }

    private applyMapFilter(): void {
        const selectedRanks = this.mapFilterRanks || [];
        if (selectedRanks.length) {
            this.filteredObjects = this.objects.filter(obj => selectedRanks.includes(obj.rank));
        } else {
            this.filteredObjects = this.objects;
        }
        this.objectCount = this.filteredObjects.length;
        this.bounds = this.computeBounds(this.filteredObjects);
        this.addMarkers(this.filteredObjects);
        if (this.showHierarchyEnabled && this.context == MapContext.THINGS) {
            let filtered = this.filteredObjects as ThingForMap[];
            let filteredIds = filtered.map(f => f.id);
            this.hierarchyButtonEnabled = filtered.some(t => t.parentThingId && filteredIds.includes(t.parentThingId));
            if (this.hierarchyButtonEnabled) {
                if (this.hierarchyFilterEnabled) {
                    this.drawHierarchyLines();
                }
            } else {
                this.hierarchyFilterEnabled = false;
                this.markersHierarchy?.clearLayers();
            }
        }
    }

    private addMarkers(objects: ThingForMap[] | LocationForMap[]): void {
        if (!this.markersCluster) {
            return;
        }
        this.closeActiveTooltip();
        this.markersCluster.clearLayers();
        const newMarkers = objects.map((obj: LocationForMap | ThingForMap) => this.createMarker(obj));
        this.markersCluster.addLayers(newMarkers);
        if (this.selectedMapAction == MapAction.MOVE) {
            this.enableMarkerMove();
        }
    }

    private createMarker(obj: ThingForMap | LocationForMap): any {
        const marker = L.marker(obj.latLng, { opacity: 0.8 });
        marker.id = obj.id;
        marker.selected = obj.selected;
        marker.rank = obj.rank;

        this.mapFilter.updateMarker(obj, marker);
        marker.bindTooltip('', { offset: [12, 0], className: this.tooltipClass, interactive: true });
        if (this.isMobile) {
            marker.off('mouseover');
            marker.off('click');
        }
        marker.off('mouseout', marker.closeTooltip, marker);
        marker.on({
            click: event => this.zone.run(() => {
                if (this.selectedMapAction == MapAction.BULK_SELECTION && this.controlKeyPressed) {
                    this.onMarkerClick(marker);
                    return;
                }
                if (this.isMobile) {
                    this.stopMobileMarkerEvent(event);
                    this.openMobileMarkerTooltip(marker, obj);
                    return;
                }
                this.onMarkerClick(marker);
            }),
            dragend: event => this.zone.run(() => this.onMarkerMoveEnd(event)),
            mouseover: () => {
                if (this.isMobile) {
                    return;
                }
                this.openDesktopTooltip(marker, obj);
            },
            mouseout: event => {
                if (this.isMobile) {
                    return;
                }
                this.startTooltipCloseIntent(marker, event.originalEvent);
            },
        });
        return marker;
    }

    private openMobileMarkerTooltip(marker: any, obj: ThingForMap | LocationForMap): void {
        if (!this.isMobile) {
            return;
        }
        this.clearTooltipCloseIntent();
        if (this.activeTooltipMarker == marker) {
            if (!marker.isTooltipOpen || !marker.isTooltipOpen()) {
                this.setTooltipPermanent(marker, true);
                marker.openTooltip();
                this.disableTooltipPreclickClose(marker);
                this.positionMarkerTooltip(marker);
                this.attachTooltipDomListeners(marker);
            }
            return;
        }
        if (this.activeTooltipMarker) {
            this.closeActiveTooltip();
        }
        marker.setOpacity(1);
        const tooltipContent = this.getMarkerTooltip(obj);
        marker.setTooltipContent(tooltipContent);
        this.setTooltipPermanent(marker, true);
        marker.openTooltip();
        this.disableTooltipPreclickClose(marker);
        this.positionMarkerTooltip(marker);
        this.activeTooltipMarker = marker;
        this.attachTooltipDomListeners(marker);
    }

    private disableTooltipPreclickClose(marker: any): void {
        const tooltip = marker.getTooltip();
        if (tooltip && this.map) {
            this.map.off('preclick', tooltip.close, tooltip);
        }
        const markerElement = marker.getElement?.();
        if (markerElement) {
            L.DomEvent.off(markerElement, 'blur', marker.closeTooltip, marker);
        }
    }

    private stopMobileMarkerEvent(event: any): void {
        if (event?.originalEvent) {
            L.DomEvent.stopPropagation(event.originalEvent);
        }
    }

    private onMapClick(event?: any): void {
        if (!this.isMobile || !this.activeTooltipMarker) {
            return;
        }
        if (this.isEventTargetInActiveTooltip(event?.originalEvent?.target)) {
            return;
        }
        this.closeActiveTooltip();
    }

    private openDesktopTooltip(marker: any, obj: ThingForMap | LocationForMap): void {
        if (this.isMobile) {
            return;
        }
        if (this.activeTooltipMarker) {
            if (this.activeTooltipMarker != marker) {
                this.closeActiveTooltip();
            } else {
                this.clearTooltipCloseIntent();
                marker.setOpacity(1);
                return;
            }
        }
        this.clearTooltipCloseIntent();
        marker.setOpacity(1);
        const tooltipContent = this.getMarkerTooltip(obj);
        marker.setTooltipContent(tooltipContent);
        this.setTooltipPermanent(marker, false);
        marker.openTooltip();
        this.positionMarkerTooltip(marker);
        this.activeTooltipMarker = marker;
        this.attachTooltipDomListeners(marker);
    }

    private positionMarkerTooltip(marker: any): void {
        const tooltip = marker.getTooltip();
        const tooltipElement = tooltip?.getElement();
        if (!tooltip || !tooltipElement || !this.map) {
            return;
        }
        const tooltipRect = tooltipElement.getBoundingClientRect();
        const mapRect = this.map.getContainer().getBoundingClientRect();
        const iconX = marker.getIcon().options.iconSize[0];
        const iconY = marker.getIcon().options.iconSize[1]

        const diffLeft = tooltipRect.left - mapRect.left - iconX;
        const diffRight = mapRect.right - tooltipRect.right - iconX;
        let xOffset = 0;
        if (diffLeft < tooltipRect.width / 2) {
            xOffset = tooltipRect.width / 2 + iconX;
        } else if (diffRight < tooltipRect.width / 2) {
            xOffset = - tooltipRect.width / 2 - iconX;
        }

        if (tooltipRect.bottom > mapRect.bottom) {
            const pos = this.map.latLngToLayerPoint(tooltip._latlng);
            tooltip.options.direction = 'top';
            tooltip.options.offset = [xOffset, -iconY];
            tooltip._setPosition(pos);
        } else if (tooltipRect.top < mapRect.top) {
            const pos = this.map.latLngToLayerPoint(tooltip._latlng);
            tooltip.options.direction = 'bottom';
            tooltip.options.offset = [xOffset, 0];
            tooltip._setPosition(pos);
        }
    }

    private attachTooltipDomListeners(marker: any): void {
        const tooltipElement = marker.getTooltip()?.getElement();
        if (!tooltipElement) {
            return;
        }
        this.detachTooltipDomListeners();
        this.activeTooltipElement = tooltipElement;
        this.attachTooltipPropagationListeners(this.activeTooltipElement);
        this.activeTooltipLinkClickListener = (event: MouseEvent) => {
            L.DomEvent.stopPropagation(event);
            this.onTooltipLinkClick(event);
        };
        this.activeTooltipElement.addEventListener('click', this.activeTooltipLinkClickListener, true);
        if (this.isMobile) {
            return;
        }
        this.tooltipMouseEnterListener = () => this.clearTooltipCloseIntent();
        this.tooltipMouseLeaveListener = () => {
            this.clearTooltipCloseIntent();
            this.tooltipCloseTimeoutId = setTimeout(() => this.closeActiveTooltip(), 100);
        };
        this.activeTooltipElement.addEventListener('mouseenter', this.tooltipMouseEnterListener);
        this.activeTooltipElement.addEventListener('mouseleave', this.tooltipMouseLeaveListener);
    }

    private startTooltipCloseIntent(marker: any, event: MouseEvent): void {
        if (this.isMobile) {
            return;
        }
        this.clearTooltipCloseIntent();
        marker.setOpacity(0.8);
        const markerElement = marker.getElement();
        const tooltipElement = marker.getTooltip()?.getElement();
        if (!event || !markerElement || !tooltipElement) {
            this.closeActiveTooltip();
            return;
        }
        const exitPoint = { x: event.clientX, y: event.clientY };
        const markerRect = markerElement.getBoundingClientRect();
        const tooltipRect = tooltipElement.getBoundingClientRect();
        const tooltipEdge = this.expandEdge(this.getTooltipEdgeFacingMarker(markerRect, tooltipRect), 10);

        this.tooltipMouseMoveListener = (mouseMoveEvent: MouseEvent) => {
            const point = { x: mouseMoveEvent.clientX, y: mouseMoveEvent.clientY };
            if (this.isPointInRect(point, tooltipRect)) {
                this.clearTooltipCloseIntent();
                return;
            }
            if (this.isPointInTriangle(point, exitPoint, tooltipEdge.a, tooltipEdge.b)) {
                return;
            }
            this.closeActiveTooltip();
        };
        document.addEventListener('mousemove', this.tooltipMouseMoveListener);
        this.tooltipMaxIntentTimeoutId = setTimeout(() => this.closeActiveTooltip(), 400);
    }

    private clearTooltipCloseIntent(): void {
        if (this.tooltipCloseTimeoutId) {
            clearTimeout(this.tooltipCloseTimeoutId);
            this.tooltipCloseTimeoutId = null;
        }
        if (this.tooltipMaxIntentTimeoutId) {
            clearTimeout(this.tooltipMaxIntentTimeoutId);
            this.tooltipMaxIntentTimeoutId = null;
        }
        if (this.tooltipMouseMoveListener) {
            document.removeEventListener('mousemove', this.tooltipMouseMoveListener);
            this.tooltipMouseMoveListener = null;
        }
    }

    private closeActiveTooltip(): void {
        this.clearTooltipCloseIntent();
        this.detachTooltipDomListeners();
        if (this.activeTooltipMarker) {
            this.activeTooltipMarker.setOpacity(0.8);
            this.resetMarkerTooltip(this.activeTooltipMarker);
            this.activeTooltipMarker.closeTooltip();
        }
        this.activeTooltipMarker = null;
        this.activeTooltipElement = null;
    }

    private detachTooltipDomListeners(): void {
        this.detachTooltipPropagationListeners();
        if (this.activeTooltipElement) {
            if (this.tooltipMouseEnterListener) {
                this.activeTooltipElement.removeEventListener('mouseenter', this.tooltipMouseEnterListener);
            }
            if (this.tooltipMouseLeaveListener) {
                this.activeTooltipElement.removeEventListener('mouseleave', this.tooltipMouseLeaveListener);
            }
            if (this.activeTooltipLinkClickListener) {
                this.activeTooltipElement.removeEventListener('click', this.activeTooltipLinkClickListener, true);
            }
        }
        this.tooltipMouseEnterListener = null;
        this.tooltipMouseLeaveListener = null;
        this.activeTooltipLinkClickListener = null;
    }

    private attachTooltipPropagationListeners(tooltipElement: HTMLElement): void {
        this.detachTooltipPropagationListeners();
        this.tooltipPropagationStopEventTypes.forEach(type => {
            const listener = (event: Event) => {
                L.DomEvent.stopPropagation(event);
            };
            tooltipElement.addEventListener(type, listener);
            this.activeTooltipPropagationListeners.push({ element: tooltipElement, type, listener, capture: false });
        });
    }

    private detachTooltipPropagationListeners(): void {
        this.activeTooltipPropagationListeners.forEach(listener => {
            listener.element.removeEventListener(listener.type, listener.listener, listener.capture);
        });
        this.activeTooltipPropagationListeners = [];
    }

    private resetMarkerTooltip(marker: any): void {
        marker.setTooltipContent('');
        const tooltipOptions = marker.getTooltip().options;
        tooltipOptions.direction = 'auto';
        tooltipOptions.offset = [12, 0];
        tooltipOptions.permanent = false;
        const markerElement = marker.getElement?.();
        if (markerElement) {
            L.DomEvent.on(markerElement, 'blur', marker.closeTooltip, marker);
        }
    }

    private setTooltipPermanent(marker: any, permanent: boolean): void {
        const tooltip = marker.getTooltip?.();
        if (tooltip) {
            tooltip.options.permanent = permanent;
        }
    }

    private isEventTargetInActiveTooltip(target: EventTarget): boolean {
        return !!target && target instanceof Node && !!this.activeTooltipElement && this.activeTooltipElement.contains(target);
    }

    private getTooltipEdgeFacingMarker(markerRect: DOMRect, tooltipRect: DOMRect): TooltipEdge {
        const markerCenter = {
            x: markerRect.left + markerRect.width / 2,
            y: markerRect.top + markerRect.height / 2
        };
        const tooltipCenter = {
            x: tooltipRect.left + tooltipRect.width / 2,
            y: tooltipRect.top + tooltipRect.height / 2
        };
        const horizontalDistance = markerCenter.x - tooltipCenter.x;
        const verticalDistance = markerCenter.y - tooltipCenter.y;
        if (Math.abs(horizontalDistance) > Math.abs(verticalDistance)) {
            if (horizontalDistance < 0) {
                return {
                    a: { x: tooltipRect.left, y: tooltipRect.top },
                    b: { x: tooltipRect.left, y: tooltipRect.bottom }
                };
            }
            return {
                a: { x: tooltipRect.right, y: tooltipRect.top },
                b: { x: tooltipRect.right, y: tooltipRect.bottom }
            };
        }
        if (verticalDistance < 0) {
            return {
                a: { x: tooltipRect.left, y: tooltipRect.top },
                b: { x: tooltipRect.right, y: tooltipRect.top }
            };
        }
        return {
            a: { x: tooltipRect.left, y: tooltipRect.bottom },
            b: { x: tooltipRect.right, y: tooltipRect.bottom }
        };
    }

    private expandEdge(edge: TooltipEdge, margin: number): TooltipEdge {
        if (edge.a.x == edge.b.x) {
            return {
                a: { x: edge.a.x, y: edge.a.y - margin },
                b: { x: edge.b.x, y: edge.b.y + margin }
            };
        }
        return {
            a: { x: edge.a.x - margin, y: edge.a.y },
            b: { x: edge.b.x + margin, y: edge.b.y }
        };
    }

    private isPointInTriangle(point: Point, a: Point, b: Point, c: Point): boolean {
        const areaSign = (p1: Point, p2: Point, p3: Point): number => {
            return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
        };
        const d1 = areaSign(point, a, b);
        const d2 = areaSign(point, b, c);
        const d3 = areaSign(point, c, a);
        const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
        const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
        return !(hasNegative && hasPositive);
    }

    private isPointInRect(point: Point, rect: DOMRect): boolean {
        return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
    }

    private getMarkerTooltip(obj: ThingForMap | LocationForMap): string {
        let tooltip = ``;
        if (this.properties.length) {
            this.properties.forEach(p => {
                const value = this.getTooltipPropertyValue(obj, p);
                let label = null;
                if (p.showLabel) {
                    label = this.localizationPipe.transform(p.label || p.name);
                }
                const link = p instanceof PropertyComponent
                    ? this.getTooltipPropertyLink(obj, p.name)
                    : null;
                tooltip += this.getTooltipRow(label, value, link);
            });
        } else { // Default tooltip
            if (this.context == MapContext.THINGS) {
                tooltip += this.getTooltipRow(
                    this.localizationPipe.transform('Name'),
                    ThingService.getValue(obj, 'name', ''),
                    { type: 'thing', id: obj.id }
                );
                tooltip += this.getTooltipRow(
                    this.localizationPipe.transform('Serial Number'),
                    ThingService.getValue(obj, 'serialNumber', ''),
                    { type: 'thing', id: obj.id }
                );
                tooltip += this.getTooltipRow(
                    this.localizationPipe.transform('Thing Definition'),
                    ThingService.getValue(obj, 'thingDefinition.name', '')
                );
            } else {
                tooltip += this.getTooltipRow(
                    this.localizationPipe.transform('Name'),
                    _.get(obj, 'name', ''),
                    { type: 'location', id: obj.id }
                );
            }
        }
        return tooltip;
    }

    private getTooltipPropertyValue(obj: ThingForMap | LocationForMap, property: PropertyComponent | CompositePartComponent): any {
        let value;
        if (property instanceof PropertyComponent) {
            if (this.context == MapContext.THINGS) {
                value = ThingService.getValue(obj, property.name, '');
            } else {
                value = _.get(obj, property.name, '');
            }
        } else { // CompositePartComponent
            value = (property as CompositePartComponent).getPropertyValues(obj);
        }
        if (property.filter) {
            value = this.loaderPipe.transform(value, property.filter, true);
        } else if (property.name == 'connectionStatus') {
            value = this.loaderPipe.transform(value, 'defaultConnectionStatus', true);
        } else if (property.name == 'cloudStatus') {
            value = this.loaderPipe.transform(value, 'defaultCloudStatus', true);
        } else if (property instanceof CompositePartComponent) {
            value = JSON.stringify(value);
        }
        return value;
    }

    private getTooltipRow(label: string, value: any, link?: { type: MapTooltipEntityType; id: string }): string {
        let row = `<div class="map-tooltip-row">`;
        if (label) {
            row += `<div class="map-tooltip-label">${label}</div>`;
        }
        if (link && link.id) {
            const escapedValue = this.escapeHtml(String(value ?? ''));
            const escapedId = this.escapeHtmlAttr(link.id);
            row += `<button type="button" class="map-tooltip-value map-tooltip-link highlight" `
                + `data-map-entity-type="${link.type}" data-map-entity-id="${escapedId}">`
                + `${escapedValue}</button>`;
        } else {
            row += `<div class="map-tooltip-value">${value}</div>`;
        }
        row += `</div>`;
        return row;
    }

    private escapeHtml(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    private escapeHtmlAttr(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    private getTooltipPropertyLink(
        obj: ThingForMap | LocationForMap,
        propertyName: string
    ): { type: MapTooltipEntityType; id: string } | null {
        if (this.context === MapContext.THINGS) {
            const thing = obj as ThingForMap;
            if (propertyName === 'name' || propertyName === 'serialNumber') {
                return thing.id ? { type: 'thing', id: thing.id } : null;
            }
            if (propertyName === 'customer.name') {
                const customerId = thing.customer?.id || thing.customerId;
                return customerId ? { type: 'customer', id: customerId } : null;
            }
            if (propertyName === 'location.name') {
                const locationId = thing.location?.id || thing.locationId;
                return locationId ? { type: 'location', id: locationId } : null;
            }
        } else {
            const location = obj as LocationForMap;
            if (propertyName === 'name') {
                return location.id ? { type: 'location', id: location.id } : null;
            }
            if (propertyName === 'customer.name') {
                const customerId = location.customer?.id;
                return customerId ? { type: 'customer', id: customerId } : null;
            }
        }
        return null;
    }

    private onTooltipLinkClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const link = target.closest('[data-map-entity-type][data-map-entity-id]') as HTMLElement | null;
        if (!link) {
            return;
        }
        event.preventDefault();
        L.DomEvent.stopPropagation(event);
        const type = link.dataset['mapEntityType'];
        const id = link.dataset['mapEntityId'];
        if (!type || !id) {
            return;
        }
        this.zone.run(() => {
            switch (type) {
                case 'thing':
                    this.navigationService.goToThingDetailPage(id);
                    break;
                case 'location':
                    this.navigationService.navigateTo(['/dashboard/location_details', id]);
                    break;
                case 'customer':
                    this.navigationService.navigateTo(['/dashboard/customer_details', id]);
                    break;
            }
        });
    }

    private onMarkerClick(marker): void {
        if (this.selectedMapAction == MapAction.BULK_SELECTION && this.controlKeyPressed) {
            const markerCoords = marker.getLatLng();
            if (marker.selected) {
                const manualIndex = this.manuallySelectedMarkers.findIndex((coords) => coords.equals(markerCoords))
                if (manualIndex == -1) {
                    // Marker is selected by area, not manually. Can't deselect
                    return;
                }
                marker.selected = false;
                this.objectsIdMapping[marker.id].selected = false;
                this?.mapFilter.updateMarker(this.objectsIdMapping[marker.id], marker);
                const idIndex = this.selectedIds.indexOf(marker.id);
                if (idIndex != -1) {
                    this.selectedIds.splice(idIndex, 1);
                }
                this.manuallySelectedMarkers.splice(manualIndex, 1);
            } else {
                marker.selected = true;
                this.objectsIdMapping[marker.id].selected = true;
                this?.mapFilter.updateMarker(this.objectsIdMapping[marker.id], marker);
                this.selectedIds.push(marker.id);
                this.manuallySelectedMarkers.push(marker.getLatLng());
            }
            this.markersCluster.refreshClusters();
            this.bulkSelectionChanged();
        } else {
            if (this.context == MapContext.THINGS) {
                this.navigationService.navigateTo(['dashboard/thing_details', marker.id]);
            } else {
                this.navigationService.navigateTo(['dashboard/location_details', marker.id]);
            }
        }
    }

    private createClusterIcon(cluster: any): any {
        const childrens = cluster.getAllChildMarkers();
        const clusterSelected = childrens.some(c => c.selected);
        const clusterRanks = childrens.map(c => c.rank);
        const maxRank = clusterRanks.reduce((prev, current) => (prev > current) ? prev : current, 0);
        const rankDescriptor = this.rankDescriptors?.find(rd => rd.rank == maxRank)
            || this.mapFilter?.getRankDescriptors(this.objects).find(rd => rd.rank == maxRank);
        let clusterClass = rankDescriptor?.clusterClass || '';
        if (clusterSelected) {
            clusterClass = rankDescriptor?.clusterSelectedClass || (clusterClass ? `${clusterClass}-selected` : '');
        }
        return L.divIcon({
            html:
                `<svg width="34px" height="34px" viewBox="-0.5 -0.5 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="16" cy="16" rx="16" ry="16" fill="none" stroke="rgb(0, 0, 0)" pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                <text x="16" y="20" fill="rgb(0, 0, 0)" text-anchor="middle">` + cluster.getChildCount() + `</text>
                </g>
                </svg>`,
            className: clusterClass,
            iconSize: [34, 34],
            iconAnchor: [17, 17],
        });
    }

    private drawHierarchyLines(): void {
        if (!this.markersHierarchy || !this.markersCluster) {
            return;
        }
        this.markersHierarchy.clearLayers();
        let filtered = this.filteredObjects as ThingForMap[];
        let filteredIds = filtered.map(f => f.id);
        this.markersCluster.eachLayer(mk => {
            const thing = this.objectsIdMapping[mk.id] as ThingForMap;
            if (thing.parentThingId && this.objectsIdMapping[thing.parentThingId] && filteredIds.includes(thing.parentThingId)) {
                const parentThing = this.objectsIdMapping[thing.parentThingId] as ThingForMap;
                let polyline = L.polyline(
                    [thing.latLng, parentThing.latLng], {
                    color: this.getHierarchyParentColor(thing.parentThingId),
                });
                this.markersHierarchy.addLayer(polyline);
            }
        });
    }

    private getHierarchyParentColor(parentId: string): string {
        if (this.hierarchyParentColorMapping[parentId]) {
            return this.hierarchyParentColorMapping[parentId];
        }
        const numColorTaken = Object.keys(this.hierarchyParentColorMapping).length;
        if (numColorTaken < HIERARCHY_COLOR_PALETTE.length) {
            this.hierarchyParentColorMapping[parentId] = HIERARCHY_COLOR_PALETTE[numColorTaken];
            return HIERARCHY_COLOR_PALETTE[numColorTaken];
        } else {
            const randomColor = '#' + Math.floor(Math.random() * 256 * 256 * 256).toString(16);
            this.hierarchyParentColorMapping[parentId] = randomColor;
            return randomColor;
        }
    }

    private createFitToContentButton(): void {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control fit-control-button');
        const button = L.DomUtil.create('a', 'leaflet-control-button', container);
        L.DomUtil.create('i', 'fas fa-expand', button);
        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, 'click', () => this.fitMapToBounds());
        container.title = 'Fit Map to Content';
        return container;
    }

    private createToggleDraggingButton(): void {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', 'leaflet-control-button', container);
        L.DomUtil.create('i', 'far fa-hand-paper', button);
        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, 'click', () => {
            const draggingEnabled = this.map.dragging._enabled;
            if (draggingEnabled) {
                button.classList.remove('leaflet-control-button-toggled');
                this.map.dragging.disable();
            } else {
                button.classList.add('leaflet-control-button-toggled');
                this.map.dragging.enable();
            }
        });
        container.title = 'Toggle Dragging';
        return container;
    }

    private computeBounds(objects: (ThingForMap | LocationForMap)[]): Bounds {
        let bounds: Bounds = null;
        objects.forEach(obj => {
            const coordinates = obj.latLng;
            if (!coordinates) {
                return;
            }
            if (!bounds) {
                bounds = {
                    ne: Object.assign({}, coordinates),
                    sw: Object.assign({}, coordinates)
                };
                return;
            }
            if (bounds.ne.lat < coordinates.lat) {
                bounds.ne.lat = coordinates.lat;
            } else if (bounds.sw.lat > coordinates.lat) {
                bounds.sw.lat = coordinates.lat;
            }

            if (bounds.ne.lng < coordinates.lng) {
                bounds.ne.lng = coordinates.lng;
            } else if (bounds.sw.lng > coordinates.lng) {
                bounds.sw.lng = coordinates.lng;
            }
        });
        return bounds;
    }

    private fitMapToBounds(): void {
        if (this.map && this.bounds) {
            const leafletBounds = L.latLngBounds(this.bounds.sw, this.bounds.ne);
            this.map.fitBounds(leafletBounds);
        }
    }

    private updateBeforePrint(): void {
        if (!this.map) return;
        this.map.invalidateSize();
        this.fitMapToBounds();
    }

    onToggleAdvancedSearch(isOpen: boolean): void {
        this.advancedSearchOpen = isOpen;
    }

    onAdvancedSearchUpdate(body: any): void {
        this.advancedSearchBody = body;
        this.disableMapAction(this.selectedMapAction);
        this.selectedMapAction = null;
        this.prevMapAction = null;
        this.bounds = null;
        this.fetchData(true);
    }

    onMapActionClick(): void {
        // Handles the uncheck of a button-toggle
        if (this.prevMapAction != this.selectedMapAction) {
            if (this.prevMapAction) {
                this.disableMapAction(this.prevMapAction);
            }
            this.prevMapAction = this.selectedMapAction;
            this.enableMapAction(this.selectedMapAction);
        } else {
            this.disableMapAction(this.selectedMapAction);
            this.selectedMapAction = null;
            this.prevMapAction = null;
        }
    }

    enableMapAction(action: MapAction): void {
        switch (action) {
            case MapAction.BULK_SELECTION:
                this.enableBulkSelection();
                break;
            case MapAction.MOVE:
                this.enableMarkerMove();
                break;
            default:
                break;
        }
    }

    disableMapAction(action: MapAction): void {
        switch (action) {
            case MapAction.BULK_SELECTION:
                this.disableBulkSelection();
                break;
            case MapAction.MOVE:
                this.disableMarkerMove();
                break;
            default:
                break;
        }
    }

    private enableBulkSelection(): void {
        if (!this.map?.selectAreaFeature) {
            return;
        }
        this.map.selectAreaFeature.enable();
    }

    private disableBulkSelection(): void {
        if (!this.map?.selectAreaFeature) {
            return;
        }
        this.map.selectAreaFeature.disable();
        this.controlKeyPressed = false;
        this.clearBulkSelection();
    }

    clearBulkSelection(): void {
        if (!this.map?.selectAreaFeature) {
            return;
        }
        this.map.selectAreaFeature.removeAllArea();
        this.selectedIds.forEach((id) => {
            if (this.objectsIdMapping[id]) {
                this.objectsIdMapping[id].selected = false;
            }
        });
        this.selectedIds = [];
        this.selectedAreas = [];
        this.manuallySelectedMarkers = [];
        this.loading = true;
        this.applyMapFilter();
        this.loading = false;
        this.refreshMapView();
    }

    @HostListener('window:keydown.control')
    onControlKeyDown(): void {
        if (this.selectedMapAction == MapAction.BULK_SELECTION && !this.controlKeyPressed) {
            this.controlKeyPressed = true;
            this.map.selectAreaFeature.disable();
        }
    }

    @HostListener('window:keyup.control')
    onControlKeyUp(): void {
        if (this.selectedMapAction == MapAction.BULK_SELECTION) {
            this.controlKeyPressed = false;
            this.map.selectAreaFeature.enable();
        }
    }

    private onMapBulkSelectionDrawEnd(): void {
        this.zone.run(() => {
            const currentlySelected = this.map.selectAreaFeature.getFeaturesSelected('marker-cluster');
            if (currentlySelected) {
                this.selectedAreas.push(this.map.selectAreaFeature.getAreaLatLng());
                currentlySelected.forEach((mk) => {
                    if (this.selectedIds.includes(mk.id)) {
                        const manualIndex = this.manuallySelectedMarkers.findIndex((coords) => coords.equals(mk.getLatLng()))
                        if (manualIndex != -1) {
                            // Since the manually selected marker is now included in an area, 
                            // it is removed it from manually selected list 
                            this.manuallySelectedMarkers.splice(manualIndex, 1);
                        }
                    } else {
                        mk.selected = true;
                        this.objectsIdMapping[mk.id].selected = true;
                        this?.mapFilter.updateMarker(this.objectsIdMapping[mk.id], mk);
                    }
                });
                this.markersCluster.refreshClusters();
                this.selectedIds = [...new Set([...this.selectedIds, ...currentlySelected.map(mk => mk.id)])];
                this.bulkSelectionChanged();
            }
        });
    }

    private bulkSelectionChanged(): void {
        if (this.selectedIds.length) {
            const things = this.selectedIds.map(id => this.objectsIdMapping[id] as Thing);
            const parentCandidates = this.mapService.getParentThingCandidates(this.allRootThings, things);
            this.assignParentThingButtonEnabled = !!parentCandidates.length;
            this.refreshMapView();
            const thingDefinitionIds = new Set(things.map(t => t.thingDefinitionId));
            this.mapService.getBulkUpdateAvailability(thingDefinitionIds).then(
                result => {
                    this.setTagButtonEnabled = result.taggingAvailable || false;
                    this.bulkCommandButtonEnabled = result.commandAvailable || false;
                    this.bulkRecipeButtonEnabled = result.recipeAvailable || false;
                    this.bulkFirmwareButtonEnabled = result.firmwareAvailable || false;
                    this.refreshMapView();
                }
            );
        }
    }

    private enableMarkerMove(): void {
        if (!this.markersCluster) {
            return;
        }
        this.markersCluster.eachLayer(mk => {
            if (mk.dragging) { // Marker shown on map
                mk.dragging.enable();
            } else { // Marker in a cluster
                mk.options.draggable = true;
            }
        });
    }

    private disableMarkerMove(): void {
        if (!this.markersCluster) {
            return;
        }
        this.markersCluster.eachLayer(mk => {
            if (mk.dragging) { // Marker shown on map
                mk.dragging.disable();
            } else { // Marker in a cluster
                mk.options.draggable = false;
            }
        });
    }

    private openDialog(componentType: ComponentType<any>, data?: any): Promise<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = false;
        dialogConfig.data = data;
        dialogConfig.minWidth = '25%';
        return firstValueFrom(this.dialog.open(componentType, dialogConfig).afterClosed());
    }

    private showSnackbar(text: string, error?: boolean): void {
        this.labelService.getCustomLabel(text)
            .then(message => {
                this.snackBar.open(this.localizationPipe.transform(message), '', {
                    duration: 4000,
                    panelClass: error ? 'notification-error' : 'notification-info'
                });
            });
    }

    private onMarkerMoveEnd(event): void {
        if (this.selectedMapAction != MapAction.MOVE) {
            return;
        }
        const newCoords = event.target.getLatLng();
        const movedObj = this.objectsIdMapping[event.target.id];
        if (!movedObj) {
            return;
        }
        movedObj.latLng = newCoords;
        movedObj.gpsPosition = `${newCoords.lat},${newCoords.lng}`;
        let updatePromise;
        if (this.context == MapContext.THINGS) {
            updatePromise = this.mapService.updateThing(movedObj as Thing);
        } else {
            updatePromise = this.mapService.updateLocation(movedObj as Location);
        }
        updatePromise.then(() => {
            if (this.context == MapContext.THINGS) {
                this.showSnackbar('moveThingProperty');
            } else {
                this.showSnackbar('moveLocationProperty');
            }
            if (this.hierarchyFilterEnabled) {
                this.drawHierarchyLines();
            }
        }).catch(() => {
            if (this.context == MapContext.THINGS) {
                this.showSnackbar('moveThingErrorProperty', true);
            } else {
                this.showSnackbar('moveLocationErrorProperty', true);
            }
        });
    }

    bulkTag(): void {
        const data: BulkUpdateTagDialogData = {
            selectedThingIds: null,
            allElementsSelected: null,
            searchParams: this.getSearchParams(),
            areaCoordinates: this.selectedAreas,
            selectedCoordinates: this.manuallySelectedMarkers,
            mapTagging: true
        }
        this.openDialog(BulkUpdateTagDialogComponent, data).then(result => {
            if (result) {
                this.showSnackbar('bulkTaggingExecutedProperty');
            }
        });
    }

    bulkUpdate(operation: string): void {
        const data: BulkOperationDialogData = {
            operationType: operation as BulkUpdateType,
            selectedThingIds: null,
            allElementsSelected: null,
            searchParams: this.getSearchParams(),
            areaCoordinates: this.selectedAreas,
            selectedCoordinates: this.manuallySelectedMarkers,
            mapTagging: true,
            bulkUpdateItems: null
        }
        this.openDialog(BulkOperationDialogComponent, data).then(result => {
            if (result) {
                if (result == 'NOW') {
                    this.showSnackbar('bulkOperationExecutedProperty');
                } else {
                    this.showSnackbar('bulkOperationScheduledProperty');
                }
            }
        });
    }

    bulkParentAssign(): void {
        const things = this.selectedIds.map(id => this.objectsIdMapping[id]);
        const data: BulkParentAssignDialogData = {
            searchParams: this.getSearchParams(),
            areaCoordinates: this.selectedAreas,
            selectedCoordinates: this.manuallySelectedMarkers,
            mapTagging: true,
            allElementsSelected: null,
            bulkUpdateParentThingItem: this.buildBulkUpdateParentThingItem(this.mapService.getParentThingCandidates(this.allRootThings, things)),
            selectedThingIds: null
        }
        this.openDialog(BulkParentAssignDialogComponent, data).then(result => {
            if (result) {
                this.showSnackbar('updateParentThingMessage');
                this.fetchData();
            }
        });
    }

    private getSearchParams(): HttpParams {
        let params = this.mapService.buildParams(this.advancedSearchBody, this.searchFields, this.showRootThings)
        return this.mapService.buildContextParams(params);
    }

    private buildBulkUpdateParentThingItem(candidates: Thing[]): BulkUpdateParentThingsResponse {
        let result: BulkUpdateParentThingsResponse = {
            multipleLocations: false,
            parentThings: {}
        }
        if (candidates && candidates.length) {
            candidates.forEach(t => result.parentThings[t.id] = t.name);
        }
        return result;
    }
}

class Bounds {
    ne: L.latLng;
    sw: L.latLng;
}

interface InitialMapData {
    locationObjects: LocationForMap[];
    filterData: { [key: string]: any[] };
}

type MapTooltipEntityType = 'thing' | 'location' | 'customer';

interface Point {
    x: number;
    y: number;
}

interface TooltipEdge {
    a: Point;
    b: Point;
}

export enum MapContext {
    THINGS = 'THINGS',
    LOCATIONS = 'LOCATIONS'
}

export enum MapAction {
    BULK_SELECTION = 'BULK_SELECTION',
    MOVE = 'MOVE'
}